import { App, Flex } from "antd";
import dayjs from "dayjs";
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useMemo,
    useState,
} from "react";
import { displayDateFormat } from "tm-shared/constants";
import { Debt, PaymentForm } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import { InvoiceInfoModal } from "@/pages/WorkPlan/Tasks/TakeDebt/InvoiceInfoModal";
import { AppSelect } from "@/shared/forms/AppSelect";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { InvoiceCard } from "../InvoiceCard";
import { StyledDebtDropdown } from "../Styled";

interface ByMarketProps {
    selectedInvoiceIds: string[];
    setSelectedInvoiceIds: Dispatch<SetStateAction<string[]>>;
    isLoading: boolean;
    invoicesById: Record<string, Debt>;
}

export const ByMarket: FC<ByMarketProps> = ({
    selectedInvoiceIds,
    setSelectedInvoiceIds,
    isLoading,
    invoicesById,
}) => {
    const [previewInvoice, setPreviewInvoice] = useState<Debt | null>(null);
    const { notification } = App.useApp();

    const invoiceOptions = useMemo(
        () =>
            Object.values(invoicesById ?? {})
                .filter(
                    (invoice) =>
                        !selectedInvoiceIds.includes(invoice.consignmentId),
                )
                .sort((a, b) => Number(b.paymentForm) - Number(a.paymentForm))
                .map((invoice) => {
                    const disabled = invoice.paymentForm === PaymentForm.Card1;

                    return {
                        value: invoice.consignmentId,
                        disabled,
                        label: (
                            <Flex
                                vertical
                                gap="10px"
                                style={{
                                    padding: "8px",
                                    width: "100%",
                                    opacity: disabled ? 0.5 : 1,
                                }}
                            >
                                <SecondaryText
                                    fontSize={18}
                                    style={{ textWrap: "wrap" }}
                                >
                                    {invoice.consignmentId}
                                </SecondaryText>
                                <Flex justify="space-between" gap="8px">
                                    <PrimaryText
                                        fontSize={18}
                                        style={{ textWrap: "wrap" }}
                                    >
                                        {dayjs(invoice.shipmentDate).format(
                                            displayDateFormat.date,
                                        )}
                                    </PrimaryText>

                                    <PrimaryText
                                        fontSize={18}
                                        textColor={
                                            disabled ? "disabled" : "accent"
                                        }
                                        style={{ textWrap: "wrap" }}
                                    >
                                        {formatUAHStandard(invoice.debt)}
                                    </PrimaryText>
                                </Flex>
                            </Flex>
                        ),
                        ...invoice,
                    };
                }),
        [invoicesById, selectedInvoiceIds],
    );

    const handleInvoiceSelect = (consignmentId: string) => {
        setSelectedInvoiceIds((prev) =>
            prev.includes(consignmentId) ? prev : [...prev, consignmentId],
        );
    };

    const handleInvoiceRemove = (consignmentId: string) => {
        if (isLoading) return;

        setSelectedInvoiceIds((prev) =>
            prev.filter((id) => id !== consignmentId),
        );
    };

    const handleInvoiceInfoClick = (invoice: Debt) => {
        setPreviewInvoice(invoice);
    };

    const handleModalClose = useCallback(() => {
        setPreviewInvoice(null);
    }, []);

    const handleCopy = useCallback(
        (debt: Debt) => {
            const { createdAt: _createdAt, context: _context, ...rest } = debt;

            copyObjectToClipboard(rest, notification);
        },
        [notification],
    );

    return (
        <>
            <Flex vertical gap={16}>
                <AppSelect
                    popupRender={(menu) => (
                        <StyledDebtDropdown>{menu}</StyledDebtDropdown>
                    )}
                    options={invoiceOptions}
                    mode="multiple"
                    value={[]}
                    onSelect={(value: string) => handleInvoiceSelect(value)}
                    placeholder="Накладна NUM"
                    disabled={isLoading}
                    size="large"
                />

                <Flex vertical gap={8}>
                    {selectedInvoiceIds.map((selectedInvoiceId) => {
                        const invoice = invoicesById[selectedInvoiceId];
                        if (!invoice) return null;

                        const { shipmentDate, debt, consignmentId } = invoice;

                        return (
                            <InvoiceCard
                                key={selectedInvoiceId}
                                consignmentId={consignmentId}
                                shipmentDate={shipmentDate}
                                debt={debt}
                                onInfo={() => handleInvoiceInfoClick(invoice)}
                                onRemove={() =>
                                    handleInvoiceRemove(selectedInvoiceId)
                                }
                                onCopy={() => handleCopy(invoice)}
                            />
                        );
                    })}
                </Flex>
            </Flex>

            <InvoiceInfoModal
                onClose={handleModalClose}
                selectedInvoice={previewInvoice}
                debt={previewInvoice?.debt ?? 0}
                shipmentDate={previewInvoice?.shipmentDate}
                open={!!previewInvoice}
            />
        </>
    );
};
